import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LandingPage from './LandingPage';
import Pricing from './Pricing';

// ── Nav height — must match across all pages ─────────────────────────────────
const NAV_H = 68;

// ── Icon ─────────────────────────────────────────────────────────────────────
const Icon = ({ path, size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={path} />
  </svg>
);

const ICONS = {
  code:      "M16 18l6-6-6-6M8 6l-6 6 6 6",
  heartbeat: "M22 12h-4l-3 9L9 3l-3 9H2",
  chart:     "M18 20V10M12 20V4M6 20v-6",
  calendar:  "M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z",
  usermd:    "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  lightbulb: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
  hands:     "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  rocket:    "M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09zM12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z",
  globe:     "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z",
  wallet:    "M21 12V7H5a2 2 0 0 1 0-4h14v4M21 12a2 2 0 0 1 0 4H5a2 2 0 0 1 0-4h16z",
  arrow:     "M5 12h14M12 5l7 7-7 7",
  quote:     "M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1zm12 0c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z",
  heart:     "M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z",
  home:      "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10",
  tag:       "M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7h.01",
  users:     "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  x:         "M18 6L6 18M6 6l12 12",
  check:     "M20 6L9 17l-5-5",
};

// ── Hooks ─────────────────────────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ── Wave divider ──────────────────────────────────────────────────────────────
const Wave = ({ color = '#ffffff', flip = false }) => (
  <div style={{ lineHeight: 0, transform: flip ? 'scaleY(-1)' : 'none', marginBottom: -1 }}>
    <svg viewBox="0 0 1440 72" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%' }}>
      <path d="M0,36 C480,72 960,0 1440,36 L1440,72 L0,72 Z" fill={color} />
    </svg>
  </div>
);

// ── Overlay Panel ─────────────────────────────────────────────────────────────
function OverlayPanel({ open, onClose, children, title }) {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', flexDirection: 'column' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(6,18,31,0.65)', backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'relative', zIndex: 1, background: '#f8faff', width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', animation: 'slideDown 0.35s cubic-bezier(0.34,1.56,0.64,1)' }}>
        <div style={{ height: 64, background: '#0a1e4a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 5vw', flexShrink: 0, boxShadow: '0 2px 24px rgba(10,30,74,0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon path={ICONS.heart} size={16} color="#7ec8f7" />
            </div>
            <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, color: '#ffffff', fontSize: 17 }}>
              ClinicPro<span style={{ color: '#7ec8f7' }}>System</span>{' '}
              <span style={{ color: 'rgba(255,255,255,0.45)', fontWeight: 400, fontSize: 14 }}>/ {title}</span>
            </span>
          </div>
          <button onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}>
            <Icon path={ICONS.x} size={18} color="#ffffff" />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}

// ── Avatar — professional initials card replacing photo ───────────────────────
function FounderAvatar({ initials, gradient, size = 110 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: gradient,
      border: '4px solid rgba(255,255,255,0.25)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Sora',sans-serif", fontWeight: 900,
      fontSize: size * 0.32, color: '#ffffff',
      letterSpacing: '-1px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
      flexShrink: 0,
      userSelect: 'none',
    }}>
      {initials}
    </div>
  );
}

// ── Timeline data ─────────────────────────────────────────────────────────────
const TIMELINE = [
  { date: 'Early 2024',  title: 'The Breaking Point',      icon: ICONS.lightbulb, body: 'After yet another chaotic day at the clinic — missed appointments, billing errors, and lost files — we sketched the first concepts for what would become ClinicProSystem.' },
  { date: 'March 2024',  title: 'First Line of Code',      icon: ICONS.code,      body: 'Development began in earnest. We focused on solving the real administrative pain points we experienced daily in a busy urban clinic.' },
  { date: 'June 2024',   title: 'First Clinic Goes Live',  icon: ICONS.heartbeat, body: 'We implemented a working version in our own clinic, refining features based on daily staff feedback and real-world challenges.' },
  { date: 'Late 2024',   title: 'First External Clients',  icon: ICONS.chart,     body: 'Two clinics adopted our system after seeing our improved efficiency and patient satisfaction metrics.' },
  { date: 'April 2025',  title: 'Official Company Launch', icon: ICONS.rocket,    body: 'Incorporated as a company and began offering ClinicProSystem to clinics nationwide.' },
];

// ── Main ──────────────────────────────────────────────────────────────────────
const AboutUs = () => {
  const navigate = useNavigate();
  const [showHome,    setShowHome]    = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  const [milestoneRef, milestoneInView] = useInView(0.1);
  const [timelineRef,  timelineInView]  = useInView(0.1);
  const [valuesRef,    valuesInView]    = useInView(0.1);
  const [founderRef,   founderInView]   = useInView(0.1);
  const [heroRef,      heroInView]      = useInView(0.05);

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: '#f8faff', color: '#0a1e4a' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800;900&family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #f8faff; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: #f0f4ff; }
        ::-webkit-scrollbar-thumb { background: #0a1e4a; border-radius: 3px; }
        @keyframes slideDown  { from { transform: translateY(-40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes pulseDot   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.55;transform:scale(1.6)} }
        @keyframes fadeUp     { from { opacity: 0; transform: translateY(32px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* ── Overlays ── */}
      <OverlayPanel open={showHome}    onClose={() => setShowHome(false)}    title="Home">    <LandingPage /> </OverlayPanel>
      <OverlayPanel open={showPricing} onClose={() => setShowPricing(false)} title="Pricing"> <Pricing />     </OverlayPanel>

      {/*
        ══ NAV — same spec as LandingPage & Pricing ══
          • position: fixed, always pinned
          • solid #0a1e4a, no border-bottom, no box-shadow
          • Logo / Home → LandingPage overlay
          • Pricing → Pricing overlay
          • Log in → /login
          • Register Free → /clinic-registration
      */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        zIndex: 300, height: NAV_H,
        background: '#0a1e4a',
        display: 'flex', alignItems: 'center',
        padding: '0 5vw',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: 1200, margin: '0 auto' }}>

          {/* Logo → Home overlay */}
          <button onClick={() => setShowHome(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
           
            <span style={{ fontSize: 20, fontWeight: 900, color: '#ffffff', letterSpacing: '-0.5px', fontFamily: "'Sora', sans-serif" }}>
              ClinicPro<span style={{ color: '#7ec8f7' }}>System</span>
            </span>
          </button>

          <nav style={{ display: 'flex', alignItems: 'center', gap: 4 }}>

            {/* Home */}
            <button onClick={() => setShowHome(true)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.78)', fontWeight: 600, fontSize: 14, cursor: 'pointer', padding: '8px 14px', borderRadius: 8, transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6 }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#ffffff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(255,255,255,0.78)'; }}>
              <Icon path={ICONS.home} size={15} color="currentColor" /> Home
            </button>

            {/* Pricing */}
            <button onClick={() => setShowPricing(true)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.78)', fontWeight: 600, fontSize: 14, cursor: 'pointer', padding: '8px 14px', borderRadius: 8, transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6 }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#ffffff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(255,255,255,0.78)'; }}>
              <Icon path={ICONS.tag} size={15} color="currentColor" /> Pricing
            </button>

            {/* Log in */}
            <button onClick={() => navigate('/login')}
              style={{ background: 'none', border: '1.5px solid rgba(255,255,255,0.3)', color: '#ffffff', fontWeight: 700, fontSize: 14, cursor: 'pointer', padding: '8px 18px', borderRadius: 8, marginLeft: 4, transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.55)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}>
              Log in
            </button>

            {/* Register Free */}
            <button onClick={() => navigate('/clinic-registration')}
              style={{ background: '#ffffff', border: 'none', color: '#0a1e4a', fontWeight: 800, fontSize: 14, cursor: 'pointer', padding: '9px 20px', borderRadius: 9, marginLeft: 4, transition: 'opacity 0.2s', boxShadow: '0 4px 16px rgba(0,0,0,0.22)' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
              Register Free
            </button>
          </nav>
        </div>
      </header>

      {/* ══ HERO ══ */}
      <section ref={heroRef} style={{
        background: 'linear-gradient(150deg,#071430 0%,#0a1e4a 50%,#1a3d82 100%)',
        paddingTop: NAV_H + 72,
        paddingBottom: 'clamp(40px,8vw,80px)',
        paddingLeft: '5vw', paddingRight: '5vw',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        {[400, 600, 800].map((s, i) => (
          <div key={i} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: s, height: s, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        ))}
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.04, pointerEvents: 'none' }} xmlns="http://www.w3.org/2000/svg">
          <defs><pattern id="adots" width="30" height="30" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.2" fill="white" /></pattern></defs>
          <rect width="100%" height="100%" fill="url(#adots)" />
        </svg>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 320, height: 320, borderRadius: '50%', background: 'rgba(58,123,213,0.18)', filter: 'blur(60px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 740, margin: '0 auto', opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(28px)', transition: 'opacity 0.7s, transform 0.7s' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 100, padding: '7px 18px', fontSize: 13, color: 'rgba(255,255,255,0.9)', marginBottom: 28, fontWeight: 600 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#5de0a0', display: 'inline-block', animation: 'pulseDot 2s infinite' }} />
            <Icon path={ICONS.calendar} size={14} color="rgba(255,255,255,0.7)" /> &nbsp;Founded April 1, 2024
          </div>
          <h1 style={{ fontSize: 'clamp(2.5rem,6vw,5rem)', fontWeight: 900, color: '#ffffff', letterSpacing: '-2.5px', margin: '0 0 20px', fontFamily: "'Sora',sans-serif", lineHeight: 1.06 }}>
            Our <span style={{ color: '#7ec8f7' }}>Story</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 'clamp(1rem,1.8vw,1.2rem)', lineHeight: 1.8, maxWidth: 580, margin: '0 auto' }}>
            How two healthcare professionals turned everyday frustration into a platform that transforms clinics across Uganda.
          </p>
        </div>
      </section>

      <Wave color="#f8faff" />

      {/* ══ ORIGIN STORY ══ */}
      <section style={{ padding: 'clamp(60px,9vw,110px) 5vw', background: '#f8faff' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 3, color: '#3a7bd5', textTransform: 'uppercase', marginBottom: 14, textAlign: 'center' }}>Why We Built This</div>
          <h2 style={{ fontSize: 'clamp(1.8rem,3.5vw,2.8rem)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 48px', fontFamily: "'Sora',sans-serif", color: '#0a1e4a', textAlign: 'center' }}>Why We Built<br /><span style={{ color: '#3a7bd5' }}>ClinicProSystem</span></h2>
          <div style={{ background: '#ffffff', borderRadius: 24, padding: 'clamp(32px,5vw,56px)', boxShadow: '0 8px 40px rgba(10,30,74,0.08)', border: '1.5px solid rgba(10,30,74,0.06)' }}>
            <p style={{ fontSize: 16, color: '#3a4a6a', lineHeight: 1.85, marginBottom: 20 }}>
              In early 2024, while working in a busy urban clinic, Clement and Jenny witnessed firsthand the administrative chaos that plagues healthcare facilities. Missing files, forgotten appointments, repetitive tiring activities, poor statistical data collection, and uncollected payments weren't just inconveniences — they were barriers to quality patient care.
            </p>
            <p style={{ fontSize: 16, color: '#3a4a6a', lineHeight: 1.85, marginBottom: 36 }}>
              After months of frustration watching these systemic issues waste staff time and compromise patient outcomes, we decided to build a solution that would address the root causes rather than just the symptoms.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 16 }}>
              {[
                { icon: ICONS.code,      label: 'First line of code',         date: 'March 2024' },
                { icon: ICONS.heartbeat, label: 'First clinic implementation', date: 'June 2024' },
                { icon: ICONS.chart,     label: 'Official company launch',     date: 'April 2025' },
              ].map(({ icon, label, date }) => (
                <div key={label} style={{ display: 'flex', gap: 14, alignItems: 'center', background: '#f8faff', borderRadius: 14, padding: '18px 20px', border: '1.5px solid rgba(10,30,74,0.08)'}}>
                  <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(10,30,74,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#0a1e4a' }}>
                    <Icon path={icon} size={18} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14, color: '#0a1e4a' }}>{label}</div>
                    <div style={{ fontSize: 12, color: '#ffffff', fontWeight: 700, marginTop: 4, background: '#0a1e4a', display: 'inline-block', padding: '2px 10px', borderRadius: 20 }}>{date}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ FOUNDERS ══ */}
      <section style={{ padding: 'clamp(60px,9vw,110px) 5vw', background: '#ffffff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 3, color: '#3a7bd5', textTransform: 'uppercase', marginBottom: 14, textAlign: 'center' }}>The Team</div>
          <h2 style={{ fontSize: 'clamp(1.8rem,3.5vw,2.8rem)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 56px', fontFamily: "'Sora',sans-serif", color: '#0a1e4a', textAlign: 'center' }}>
            The Faces Behind<br /><span style={{ color: '#3a7bd5' }}>ClinicProSystem</span>
          </h2>

          <div ref={founderRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px,1fr))', gap: 28 }}>
            {[
              {
                name: 'Ddungu Clement',
                initials: 'DC',
                gradient: 'linear-gradient(135deg, #0a1e4a 0%, #1a3d82 60%, #3a7bd5 100%)',
                role: 'Founder & Lead Developer',
                roleColor: '#7ec8f7',
                skills: ['Software Engineering', 'Healthcare Operations', 'System Architecture'],
                bio: "With a background in both healthcare and software engineering, Clement brings a unique perspective to clinic management solutions. His experience working in understaffed clinics showed him exactly where technology could make the biggest impact.",
                quote: "I watched good clinics struggle not because of poor medical care, but because of preventable administrative failures. That's what ClinicProSystem was born to fix.",
                delay: 0,
              },
              {
                name: 'Kisaakye Jenny',
                initials: 'KJ',
                gradient: 'linear-gradient(135deg, #1a3d82 0%, #3a7bd5 60%, #7ec8f7 100%)',
                role: 'Co-Founder & Operations Lead',
                roleColor: '#7ec8f7',
                skills: ['Clinic Finance', 'Patient Relations', 'Operations Management'],
                bio: "Jenny's front-line experience as a clinic cashier gave her deep insight into the financial and communication challenges clinics face daily. She ensures our solutions work in real-world conditions.",
                quote: "When you're juggling patient care and paperwork, you need systems that adapt to you — not the other way around. That's our design philosophy.",
                delay: 0.12,
              },
            ].map(({ name, initials, gradient, role, roleColor, skills, bio, quote, delay }) => (
              <div key={name}
                style={{ background: '#f8faff', borderRadius: 24, overflow: 'hidden', border: '1.5px solid rgba(10,30,74,0.07)', boxShadow: '0 4px 24px rgba(10,30,74,0.07)', transition: 'transform 0.25s, box-shadow 0.25s', opacity: founderInView ? 1 : 0, transform: founderInView ? 'translateY(0)' : 'translateY(32px)', transitionDelay: `${delay}s`, transitionProperty: 'opacity, transform, box-shadow' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 20px 56px rgba(10,30,74,0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(10,30,74,0.07)'; }}>

                {/* Top navy strip — avatar + name (no photo, pure design) */}
                <div style={{ background: 'linear-gradient(135deg,#0a1e4a,#1a3d82)', padding: '40px 32px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', overflow: 'hidden' }}>
                  {/* Decorative rings */}
                  {[130, 180].map((s, i) => (
                    <div key={i} style={{ position: 'absolute', width: s, height: s, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.07)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />
                  ))}
                  <div style={{ position: 'relative', zIndex: 1, marginBottom: 18 }}>
                    <FounderAvatar initials={initials} gradient={gradient} size={108} />
                    {/* Online indicator dot */}
                    <div style={{ position: 'absolute', bottom: 6, right: 6, width: 16, height: 16, borderRadius: '50%', background: '#5de0a0', border: '3px solid #0a1e4a' }} />
                  </div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 20, color: '#ffffff', marginBottom: 4, position: 'relative', zIndex: 1 }}>{name}</div>
                  <div style={{ fontSize: 13, color: roleColor, fontWeight: 600, marginBottom: 18, position: 'relative', zIndex: 1 }}>{role}</div>
                  {/* Skill chips */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
                    {skills.map(s => (
                      <span key={s} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 20, padding: '4px 12px', fontSize: 11, color: 'rgba(255,255,255,0.8)', fontWeight: 600, letterSpacing: 0.3 }}>{s}</span>
                    ))}
                  </div>
                </div>

                {/* Card body */}
                <div style={{ padding: '28px 32px 32px' }}>
                  <p style={{ fontSize: 14.5, color: '#3a4a6a', lineHeight: 1.78, marginBottom: 22 }}>{bio}</p>
                  <div style={{ background: '#ffffff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 12px rgba(10,30,74,0.06)' }}>
                    <Icon path={ICONS.quote} size={24} color="rgba(10,30,74,0.14)" />
                    <p style={{ fontSize: 14, color: '#4a5c82', lineHeight: 1.75, fontStyle: 'italic', margin: '10px 0 0' }}>{quote}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TIMELINE ══ */}
      <div style={{ background: '#ffffff' }}><Wave color="#0a1e4a" /></div>
      <section style={{ background: '#0a1e4a', padding: 'clamp(60px,9vw,110px) 5vw' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 3, color: '#7ec8f7', textTransform: 'uppercase', marginBottom: 14, textAlign: 'center' }}>Journey</div>
          <h2 style={{ fontSize: 'clamp(1.8rem,3.5vw,2.8rem)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 56px', fontFamily: "'Sora',sans-serif", color: '#ffffff', textAlign: 'center' }}>
            Our Development<br /><span style={{ color: '#7ec8f7' }}>Journey</span>
          </h2>
          <div ref={timelineRef} style={{ position: 'relative', paddingLeft: 32 }}>
            {/* Vertical line */}
            <div style={{ position: 'absolute', left: 15, top: 8, bottom: 8, width: 2, background: 'linear-gradient(to bottom, rgba(126,200,247,0.1), rgba(126,200,247,0.3), rgba(126,200,247,0.1))', borderRadius: 1 }} />
            {TIMELINE.map(({ date, title, icon, body }, i) => (
              <div key={i} style={{ position: 'relative', marginBottom: i < TIMELINE.length - 1 ? 36 : 0, opacity: timelineInView ? 1 : 0, transform: timelineInView ? 'translateX(0)' : 'translateX(-24px)', transition: `opacity 0.55s ${i * 0.1}s, transform 0.55s ${i * 0.1}s` }}>
                {/* Timeline dot */}
                <div style={{ position: 'absolute', left: -25, top: 20, width: 22, height: 22, borderRadius: '50%', background: '#7ec8f7', border: '3px solid #0a1e4a', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, boxShadow: '0 0 0 4px rgba(126,200,247,0.15)' }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#0a1e4a' }} />
                </div>
                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 18, padding: '24px 28px', backdropFilter: 'blur(8px)', transition: 'background 0.2s, border-color 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.09)'; e.currentTarget.style.borderColor = 'rgba(126,200,247,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; }}>
                  <div style={{ display: 'inline-block', background: '#7ec8f7', color: '#0a1e4a', fontSize: 12, fontWeight: 800, padding: '4px 12px', borderRadius: 20, marginBottom: 14, letterSpacing: 0.5 }}>{date}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(126,200,247,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon path={icon} size={17} color="#7ec8f7" />
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 16, color: '#ffffff', fontFamily: "'Sora',sans-serif" }}>{title}</div>
                  </div>
                  <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.72, margin: 0 }}>{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Wave color="#f8faff" flip />

      {/* ══ SPECIAL THANKS ══ */}
      <section style={{ padding: 'clamp(60px,9vw,110px) 5vw', background: '#f8faff' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 3, color: '#3a7bd5', textTransform: 'uppercase', marginBottom: 14, textAlign: 'center' }}>Gratitude</div>
          <h2 style={{ fontSize: 'clamp(1.8rem,3.5vw,2.8rem)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 48px', fontFamily: "'Sora',sans-serif", color: '#0a1e4a', textAlign: 'center' }}>
            Special Thanks to Our<br /><span style={{ color: '#3a7bd5' }}>Early Clients</span>
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px,1fr))', gap: 24 }}>
            {[
              { initial: 'G', gradient: 'linear-gradient(135deg,#0a1e4a,#3a7bd5)', name: 'Madam Getrude', title: 'CEO, LIFESURE MEDICARE', body: "We are eternally grateful to Madam Getrude for giving us the opportunity to test our early application at LIFESURE MEDICARE. Her facility became our proving ground, allowing us to discover crucial loopholes and make necessary improvements." },
              { initial: 'S', gradient: 'linear-gradient(135deg,#1a3d82,#7ec8f7)', name: 'Madam Sarah Kikajjo', title: 'Director, Health Center & Maternity Home', body: "Our heartfelt thanks to Madam Sarah Namyalo for her trust during the earliest days of our application, when we had no proven track record. Her faith in our vision gave us the confidence to push forward." },
            ].map(({ initial, gradient, name, title, body }) => (
              <div key={name}
                style={{ background: '#ffffff', borderRadius: 20, padding: '32px 28px', border: '1.5px solid rgba(10,30,74,0.07)', boxShadow: '0 4px 24px rgba(10,30,74,0.07)', transition: 'transform 0.2s, box-shadow 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 14px 44px rgba(10,30,74,0.13)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(10,30,74,0.07)'; }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 24, color: '#ffffff', marginBottom: 20, fontFamily: "'Sora',sans-serif", boxShadow: '0 4px 16px rgba(10,30,74,0.2)' }}>
                  {initial}
                </div>
                <div style={{ fontWeight: 800, fontSize: 17, color: '#0a1e4a', marginBottom: 4, fontFamily: "'Sora',sans-serif" }}>{name}</div>
                <div style={{ fontSize: 12, color: '#3a7bd5', fontWeight: 700, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
                <p style={{ fontSize: 14, color: '#4a5c82', lineHeight: 1.75, margin: 0 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CORE BELIEFS ══ */}
      <section style={{ padding: 'clamp(60px,9vw,110px) 5vw', background: '#ffffff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 3, color: '#3a7bd5', textTransform: 'uppercase', marginBottom: 14, textAlign: 'center' }}>Our Values</div>
          <h2 style={{ fontSize: 'clamp(1.8rem,3.5vw,2.8rem)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 56px', fontFamily: "'Sora',sans-serif", color: '#0a1e4a', textAlign: 'center' }}>
            Our Core <span style={{ color: '#3a7bd5' }}>Beliefs</span>
          </h2>
          <div ref={valuesRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 24 }}>
            {[
              { icon: ICONS.usermd,    title: 'Healthcare First',     body: "We're clinicians first, technologists second. Every feature serves the ultimate goal of better patient outcomes.", delay: 0 },
              { icon: ICONS.lightbulb, title: 'Practical Innovation', body: "If a solution doesn't work in a busy Monday morning clinic, it doesn't belong in our system.", delay: 0.12 },
              { icon: ICONS.hands,     title: 'Partnership Approach', body: "We grow by helping our clients succeed, not by locking them into restrictive contracts.", delay: 0.24 },
            ].map(({ icon, title, body, delay }) => (
              <div key={title}
                style={{ background: '#f8faff', borderRadius: 20, padding: '36px 28px', textAlign: 'center', border: '1.5px solid rgba(10,30,74,0.07)', boxShadow: '0 4px 20px rgba(10,30,74,0.06)', cursor: 'default', opacity: valuesInView ? 1 : 0, transform: valuesInView ? 'translateY(0)' : 'translateY(28px)', transition: `opacity 0.55s ${delay}s, transform 0.55s ${delay}s, background 0.3s` }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = '#0a1e4a';
                  e.currentTarget.querySelectorAll('.val-title').forEach(el => el.style.color = '#ffffff');
                  e.currentTarget.querySelectorAll('.val-body').forEach(el => el.style.color = 'rgba(255,255,255,0.7)');
                  e.currentTarget.querySelectorAll('.val-icon-wrap').forEach(el => { el.style.background = 'rgba(255,255,255,0.12)'; el.style.color = '#7ec8f7'; });
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = '#f8faff';
                  e.currentTarget.querySelectorAll('.val-title').forEach(el => el.style.color = '#0a1e4a');
                  e.currentTarget.querySelectorAll('.val-body').forEach(el => el.style.color = '#4a5c82');
                  e.currentTarget.querySelectorAll('.val-icon-wrap').forEach(el => { el.style.background = 'rgba(10,30,74,0.07)'; el.style.color = '#0a1e4a'; });
                }}>
                <div className="val-icon-wrap" style={{ width: 68, height: 68, borderRadius: '50%', background: 'rgba(10,30,74,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px', color: '#0a1e4a', transition: 'background 0.3s, color 0.3s' }}>
                  <Icon path={icon} size={28} />
                </div>
                <div className="val-title" style={{ fontWeight: 800, fontSize: 17, color: '#0a1e4a', marginBottom: 12, fontFamily: "'Sora',sans-serif", transition: 'color 0.3s' }}>{title}</div>
                <p className="val-body" style={{ fontSize: 14.5, color: '#4a5c82', lineHeight: 1.72, margin: 0, transition: 'color 0.3s' }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ MILESTONES ══ */}
      <div style={{ background: '#ffffff' }}><Wave color="#0a1e4a" /></div>
      <section ref={milestoneRef} style={{ background: '#0a1e4a', padding: 'clamp(60px,9vw,110px) 5vw' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 3, color: '#7ec8f7', textTransform: 'uppercase', marginBottom: 14, textAlign: 'center' }}>Results</div>
          <h2 style={{ fontSize: 'clamp(1.8rem,3.5vw,2.8rem)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 56px', fontFamily: "'Sora',sans-serif", color: '#ffffff', textAlign: 'center' }}>
            Our <span style={{ color: '#7ec8f7' }}>Milestones</span>
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 2, borderRadius: 20, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
            {[
              { val: '12+',  label: 'Clinics Transformed' },
              { val: '100%', label: 'Client Retention Rate' },
              { val: '40%',  label: 'Fewer Missed Appointments' },
              { val: '60%',  label: 'Improved Clinic Management' },
            ].map(({ val, label }, i) => (
              <div key={label} style={{ background: 'rgba(255,255,255,0.04)', padding: '48px 24px', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.06)', opacity: milestoneInView ? 1 : 0, transform: milestoneInView ? 'translateY(0)' : 'translateY(24px)', transition: `opacity 0.55s ${i * 0.1}s, transform 0.55s ${i * 0.1}s` }}>
                <div style={{ fontSize: 'clamp(2.5rem,5vw,4rem)', fontWeight: 900, color: '#7ec8f7', fontFamily: "'Sora',sans-serif", letterSpacing: '-2px', lineHeight: 1 }}>{val}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 14, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Wave color="#f8faff" flip />

      {/* ══ FUTURE GOALS ══ */}
      <section style={{ padding: 'clamp(60px,9vw,110px) 5vw', background: '#f8faff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 3, color: '#3a7bd5', textTransform: 'uppercase', marginBottom: 14, textAlign: 'center' }}>Roadmap</div>
          <h2 style={{ fontSize: 'clamp(1.8rem,3.5vw,2.8rem)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 56px', fontFamily: "'Sora',sans-serif", color: '#0a1e4a', textAlign: 'center' }}>
            Where We're <span style={{ color: '#3a7bd5' }}>Headed</span>
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 24 }}>
            {[
              { emoji: '🌍', title: 'National Expansion',  body: 'Bringing ClinicProSystem to clinics across the country — every facility deserves great management tools.' },
              { emoji: '🤖', title: 'AI Advancements',     body: 'Developing predictive analytics for patient no-shows, treatment outcomes, and smarter decision support.' },
              { emoji: '💳', title: 'Life Wallet',          body: 'An Android/iOS app to help patients save an emergency fund for unforeseen medical expenses.' },
            ].map(({ emoji, title, body }) => (
              <div key={title}
                style={{ background: '#ffffff', borderRadius: 20, padding: '36px 28px', border: '1.5px solid rgba(10,30,74,0.07)', boxShadow: '0 4px 24px rgba(10,30,74,0.07)', transition: 'transform 0.2s, box-shadow 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(10,30,74,0.14)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(10,30,74,0.07)'; }}>
                <div style={{ fontSize: 44, marginBottom: 20 }}>{emoji}</div>
                <div style={{ fontWeight: 800, fontSize: 17, color: '#0a1e4a', marginBottom: 12, fontFamily: "'Sora',sans-serif" }}>{title}</div>
                <p style={{ fontSize: 14.5, color: '#4a5c82', lineHeight: 1.75, margin: 0 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section style={{ background: 'linear-gradient(150deg,#071430,#0a1e4a,#1a3d82)', padding: 'clamp(70px,12vw,120px) 5vw', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 600, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 640, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(2rem,4vw,3.2rem)', fontWeight: 900, color: '#ffffff', letterSpacing: '-1.5px', margin: '0 0 16px', fontFamily: "'Sora',sans-serif" }}>
            Join Our <span style={{ color: '#7ec8f7' }}>Story</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 16, lineHeight: 1.75, maxWidth: 520, margin: '0 auto 40px' }}>
            We're always looking to partner with clinics that share our vision for better healthcare through smarter management.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/clinic-registration')}
              style={{ padding: '16px 40px', borderRadius: 12, border: 'none', background: '#ffffff', color: '#0a1e4a', fontWeight: 800, fontSize: 16, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 10, boxShadow: '0 8px 40px rgba(0,0,0,0.25)', transition: 'transform 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
              Become Part of Our Journey <Icon path={ICONS.arrow} size={18} color="#0a1e4a" />
            </button>
            <button onClick={() => setShowPricing(true)}
              style={{ padding: '16px 32px', borderRadius: 12, border: '1.5px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.08)', color: '#ffffff', fontWeight: 700, fontSize: 16, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.16)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}>
              View Pricing <Icon path={ICONS.tag} size={15} color="#7ec8f7" />
            </button>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer style={{ background: '#06121f', padding: 'clamp(40px,6vw,60px) 5vw 28px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: 40, paddingBottom: 40, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div>
              <button onClick={() => setShowHome(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: '#0a1e4a', border: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon path={ICONS.heart} size={15} color="#7ec8f7" />
                </div>
                <span style={{ fontSize: 17, fontWeight: 900, color: '#ffffff', fontFamily: "'Sora',sans-serif" }}>
                  ClinicPro<span style={{ color: '#7ec8f7' }}>System</span>
                </span>
              </button>
              <p style={{ fontSize: 13.5, color: '#445577', lineHeight: 1.8 }}>Modern AI-powered clinic management. Built for Uganda.</p>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2.5, color: '#7ec8f7', textTransform: 'uppercase', marginBottom: 18 }}>Quick Links</div>
              {[
                ['Home',          () => setShowHome(true)],
                ['Pricing',       () => setShowPricing(true)],
                ['Register Free', () => navigate('/clinic-registration')],
                ['Log in',        () => navigate('/login')],
              ].map(([l, fn]) => (
                <button key={l} onClick={fn}
                  style={{ display: 'block', fontSize: 14, color: '#445577', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 10px', textAlign: 'left', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#ffffff'}
                  onMouseLeave={e => e.currentTarget.style.color = '#445577'}>{l}</button>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2.5, color: '#7ec8f7', textTransform: 'uppercase', marginBottom: 18 }}>Contact</div>
              <p style={{ fontSize: 14, color: '#445577', lineHeight: 2.1 }}>
                +256 703 521 101<br />+256 786 747 733<br />clinicproug@gmail.com
              </p>
            </div>
          </div>
          <div style={{ paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <span style={{ fontSize: 13, color: '#2a3a55' }}>© 2025 ClinicProSystem. All rights reserved.</span>
            <span style={{ fontSize: 13, color: '#2a3a55' }}>Built with ❤ in Uganda</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;